import { toast } from "sonner";

/**
 * toastErrorWithHelp — sonner `toast.error` wrapper that adds an automatic
 * "Get help →" action button. Clicking it opens `/status?probe=<inferred>&desc=<message>`
 * in a new tab so the user lands directly on the matching probe + a
 * prefilled support description.
 *
 * The probe key can be passed explicitly via `opts.probe`, OR it's
 * inferred from the title/description text using simple keyword matching
 * (`inferProbe` below). When inference can't decide it returns "" so the
 * deep-link still prefills the description without highlighting a probe.
 *
 * Usage:
 *   toastErrorWithHelp("Cannot join collab room", {
 *     description: error?.message,
 *     // probe: "websocket",  // optional, auto-inferred from "collab"
 *   });
 *
 * Notes on design:
 *  - Pure wrapper — falls back to vanilla `toast.error` if sonner's `action`
 *    option ever changes shape, so callers can adopt this without fear.
 *  - The deep-link opens in a new tab (`noopener,noreferrer`) so the user
 *    doesn't lose the studio context they were in when the error fired.
 *  - We cap `desc` at 240 chars when building the URL — beyond that, the
 *    URL becomes unwieldy and most users won't read it anyway.
 */

/** Map a free-text message to a /status probe key. Returns `""` if nothing
 *  matched. Keep this list small + obvious — every entry is a substring
 *  match against the lowercased title+description, so order is fine. */
function inferProbe(text) {
  const t = (text || "").toLowerCase();
  if (!t) return "";
  if (/(collab|room|peer|ws|websocket|signaling)/.test(t)) return "websocket";
  if (/(stun|turn|ice|nat|rtcpeerconnection|webrtc)/.test(t)) return "stun";
  if (/(microphone|getusermedia|mic|input device|enumeratedevices)/.test(t)) return "media";
  if (/(audiocontext|web audio|playback|sample rate|samplerate)/.test(t)) return "audio";
  if (/(mongo|database|backend|api|status|\/api)/.test(t)) return "backend";
  if (/(yjs|crdt|y\.doc|y\.map|sync)/.test(t)) return "yjs";
  return "";
}

/** Build the /status deep-link URL with optional probe + description. */
function buildHelpURL({ probe, desc }) {
  const url = new URL("/status", window.location.origin);
  if (probe) url.searchParams.set("probe", probe);
  if (desc) url.searchParams.set("desc", desc.slice(0, 240));
  return url.toString();
}

export function toastErrorWithHelp(title, opts = {}) {
  const description = opts.description;
  const probe = opts.probe ?? inferProbe(`${title} ${description || ""}`);
  // Compose the description to ship to /status — prefer the structured
  // description if present, otherwise fall back to the title.
  const helpDesc = String(description || title || "").trim();
  const helpUrl = buildHelpURL({ probe, desc: helpDesc });
  try {
    toast.error(title, {
      ...opts,
      duration: opts.duration ?? 6500,
      action: opts.action ?? {
        label: "Get help →",
        onClick: () => window.open(helpUrl, "_blank", "noopener,noreferrer"),
      },
    });
  } catch (_e) { void _e;
    // Defensive — if sonner's API ever changes, degrade to a plain error.
    toast.error(title, { description });
  }
}

// Expose the underlying helpers for testing + advanced callers (e.g. when
// you want to programmatically open the help URL without showing a toast).
toastErrorWithHelp.inferProbe = inferProbe;
toastErrorWithHelp.buildHelpURL = buildHelpURL;

export default toastErrorWithHelp;
